import * as React from "react"
import { ArrowLeft } from "lucide-react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { AlfredClientConfigEditor } from "@/components/alfred/alfred-client-config-editor"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { ApiErrorState, ClientNotFoundState } from "@/components/shared/empty-states"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useAlfredClients, useUpdateAlfredClient } from "@/hooks/use-alfred-clients"
import { useToast } from "@/hooks/use-toast"
import { isAlfredClientListOrigin } from "@/lib/alfred-client-navigation"
import type { UpdateAlfredClientRequest } from "@/types/alfred-client"

export function AlfredClientEditPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: clients, error, isLoading, mutate: reloadClients } = useAlfredClients()
  const updateClient = useUpdateAlfredClient()
  const [isDirty, setIsDirty] = React.useState(false)
  const [showDiscardConfirmation, setShowDiscardConfirmation] = React.useState(false)
  const numericId = Number(id)
  const validId = Number.isInteger(numericId) && numericId > 0
  const client = validId ? clients?.find((candidate) => candidate.id === numericId) : undefined

  const returnToClients = () => {
    if (isAlfredClientListOrigin(location.state)) {
      navigate(-1)
      return
    }
    navigate("/alfred/clients")
  }

  const requestBack = () => {
    if (isDirty) {
      setShowDiscardConfirmation(true)
      return
    }
    returnToClients()
  }

  const handleSave = async (data: UpdateAlfredClientRequest) => {
    if (!client) return
    try {
      // The mutation hook revalidates the shared client-list cache.
      await updateClient.trigger({ id: client.id, data })
    } catch (saveError) {
      toast({
        variant: "error",
        title: "Failed to update client",
        description: saveError instanceof Error ? saveError.message : "Please try again.",
      })
      return
    }
    toast({
      variant: "success",
      title: "Client updated successfully",
      description: `Client ${client.clientId} has been updated.`,
    })
    returnToClients()
  }

  return (
    <>
      <DashboardHeader />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={requestBack}
            disabled={updateClient.isMutating}
            className="text-muted-foreground hover:text-foreground hover:bg-secondary -ml-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Clients
          </Button>
          <h1 className="text-2xl font-semibold text-foreground">Edit client</h1>
          {client ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Update client configuration for <span className="font-mono">{client.clientId}</span>.
            </p>
          ) : null}
        </div>

        {!validId ? <ClientNotFoundState />
          : error ? <ApiErrorState onRetry={() => void reloadClients()} />
          : isLoading ? <DevicesTableSkeleton />
          : !client ? <ClientNotFoundState />
          : (
            <AlfredClientConfigEditor
              key={client.id}
              mode="edit"
              client={client}
              onSubmit={handleSave}
              isSubmitting={updateClient.isMutating}
              onDirtyChange={setIsDirty}
            />
          )}
      </main>

      <AlertDialog open={showDiscardConfirmation} onOpenChange={setShowDiscardConfirmation}>
        <AlertDialogContent className="max-w-[400px] border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>Your changes to this client will be lost.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={returnToClients}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
